import { TokenSelector } from "components/TokenSelector/TokenSelector";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { MAX_SELECTABLE_TOKENS } from "config/consts";
import { useTotalUSDAmount } from "hooks/useTotalUsdAmount";
import { TotalTradeAmount } from "./TotalTradeAmount";
import { AmountBar } from "components/AmountBar/AmountBar";
import styled from "@emotion/styled";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import { TradeLegTaker } from "components/TradeLeg/TradeLegTaker";
import { WarningOrError } from "../../../components/WarningOrError/WarningOrError";
import { getTradeErrorFromQuote, TRADE_SIZE_ERROR_CODE } from "../../../config/errorCodes";
import { SwitchTokensButton } from "components/ButterComponents/SwitchTokens/SwitchTokens";
import { Amount } from "types/amount";
import { Hash } from "viem";
import { useOnClickOutside } from "usehooks-ts";

export const TakerSection: React.FC = () => {
  const takerTokens = GlobalStore.useState(s => s.takerTokens);
  const makerTokens = GlobalStore.useState(s => s.makerTokens);
  const quoteTakerTokens = GlobalStore.useState(s => s.quote?.takerTokens);
  const quoteError = GlobalStore.useState(s => s.quoteError);
  const orderError = GlobalStore.useState(s => s.orderError);
  const chain = GlobalStore.useState(s => s.network);
  const totalUsdAmount = useTotalUSDAmount(takerTokens, false, quoteTakerTokens);
  const tokenList = useTokenListByChain();
  const selectorRef = useRef<HTMLDivElement>(null);
  const [isDropdownDisplayed, setIsDropdownDisplayed] = useState(false);
  const [isWaitingToAddToken, setIsWaitingToAddToken] = useState(false);
  const [isChangingToken, setIsChangingToken] = useState<Hash | undefined>(undefined);
  const showSelector = takerTokens.length === 0 || isChangingToken || isWaitingToAddToken;
  const errorMessageFromQuoteError = getTradeErrorFromQuote(chain, quoteError);
  const [expandedLeg, setExpandedLeg] = useState<Hash | undefined>(undefined);
  const prevNumberOfLegs = useRef(takerTokens.length);
  const hasAddedMultipleLegs = takerTokens.length - prevNumberOfLegs.current > 1;

  useOnClickOutside(selectorRef, () => {
    setIsDropdownDisplayed(false);
    setIsChangingToken(undefined);
    setIsWaitingToAddToken(false);
  });

  useEffect(() => {
    setIsWaitingToAddToken(false);
    setIsChangingToken(undefined);
    //   If more than one leg was added - it was programmatically - don't expand legs.
    if (hasAddedMultipleLegs) {
      setExpandedLeg(undefined);
    } else if (prevNumberOfLegs.current !== takerTokens.length) {
      setExpandedLeg(takerTokens?.[takerTokens.length - 1]?.tokenAddress ?? undefined);
    }
  }, [hasAddedMultipleLegs, takerTokens]);

  useEffect(() => {
    // Update the number of legs
    prevNumberOfLegs.current = takerTokens.length;
  }, [takerTokens.length]);

  const handleOnFocus = useCallback((id: Hash) => {
    setExpandedLeg(id);
  }, []);

  const handleCollapse = useCallback(
    (id: Hash) => {
      if (expandedLeg === id) {
        setExpandedLeg(undefined);
      }
    },
    [expandedLeg]
  );

  return (
    <>
      {takerTokens.map((entry, index) => {
        const lastIndex = takerTokens.length - 1;
        const showPlusButton =
          index === lastIndex && makerTokens.length <= 1 && index !== MAX_SELECTABLE_TOKENS - 1;
        if (isChangingToken === entry.tokenAddress) {
          return (
            <TokenSelector
              ref={selectorRef}
              isChangingToken={isChangingToken}
              counterParty="taker"
              isDropdownDisplayed={isDropdownDisplayed}
              setIsDropdownDisplayed={setIsDropdownDisplayed}
            />
          );
        }
        return (
          <TradeLegTaker
            key={entry.tokenAddress}
            tokenAddress={entry.tokenAddress}
            showPlusButton={showPlusButton}
            isWaitingToAddToken={isWaitingToAddToken}
            setIsWaitingToAddToken={setIsWaitingToAddToken}
            setIsChangingToken={setIsChangingToken}
            onFocus={handleOnFocus}
            onCollapse={handleCollapse}
            isExpanded={expandedLeg === entry.tokenAddress}
          />
        );
      })}

      {showSelector && !isChangingToken && (
        <TokenSelector
          ref={selectorRef}
          isChangingToken={isChangingToken}
          counterParty="taker"
          isDropdownDisplayed={isDropdownDisplayed}
          setIsDropdownDisplayed={setIsDropdownDisplayed}
        />
      )}
      {takerTokens.length > 1 && (
        <AmountBarContainer>
          <AmountBar counterParty={"taker"} totalUsdAmount={totalUsdAmount} />
          <TotalTradeAmount isVisible={tokenList !== undefined} totalUsdAmount={totalUsdAmount} />
        </AmountBarContainer>
      )}
      {errorMessageFromQuoteError &&
        quoteError?.error.errorCode === TRADE_SIZE_ERROR_CODE &&
        orderError === undefined && (
          <WarningOrError isTradeSizeError={true} {...errorMessageFromQuoteError} />
        )}
      <SwitchTokensButton
        isVisible={true}
        onClick={() => {
          switchTradeTokens();
        }}
      />
    </>
  );
};

// Sub-components

const AmountBarContainer = styled.div`
  display: flex;
  padding: 12px 26px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

// Helpers

const switchTradeTokens = () => {
  GlobalStore.update(s => {
    const makerTokens = [...s.makerTokens];
    const takerTokens = [...s.takerTokens];
    s.makerTokens = takerTokens.map(token => {
      return {
        ...token,
        pct: 100 / takerTokens.length,
        isLocked: false,
        amount: Amount.ZERO,
      };
    });
    s.takerTokens = makerTokens;
    // Clear quote if there was one
    s.quote = undefined;
  });
};
